import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Trash2, Plus, Calendar, Package, AlertTriangle, Ban } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RebusPage = () => {
  const { getAuthHeaders } = useAuth();
  const [rebusList, setRebusList] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNewRebusModal, setShowNewRebusModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rebusToDelete, setRebusToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [newRebus, setNewRebus] = useState({
    date: new Date().toISOString().split('T')[0],
    aggloType: 'agglo_10',
    quantity: '',
    reason: ''
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rebusRes, summaryRes] = await Promise.all([
        axios.get(`${API}/rebus`, getAuthHeaders()),
        axios.get(`${API}/rebus/summary`, getAuthHeaders())
      ]);
      setRebusList(rebusRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching rebus:', error);
      toast.error('Erreur lors du chargement des rebus');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRebus = async () => {
    if (!newRebus.quantity || parseInt(newRebus.quantity) <= 0) {
      toast.error('Veuillez saisir une quantité valide');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/rebus`,
        {
          agglo_type: newRebus.aggloType,
          quantity: parseInt(newRebus.quantity),
          reason: newRebus.reason || null,
          date: newRebus.date
        },
        getAuthHeaders()
      );

      toast.success(`${newRebus.quantity} ${getAggloTypeLabel(newRebus.aggloType)} marqués comme rebus`);
      setShowNewRebusModal(false);
      setNewRebus({
        date: new Date().toISOString().split('T')[0],
        aggloType: 'agglo_10',
        quantity: '',
        reason: ''
      });
      fetchAll();
    } catch (error) {
      console.error('Error creating rebus:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du rebus');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRebus = async () => {
    if (!rebusToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/rebus/${rebusToDelete.id}`, getAuthHeaders());
      toast.success('Rebus supprimé');
      setRebusToDelete(null);
      fetchAll();
    } catch (error) {
      console.error('Error deleting rebus:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const getAggloTypeLabel = (type) => {
    const labels = {
      'agglo_10': 'Agglo de 10',
      'agglo_15': 'Agglo de 15',
      'agglo_20': 'Agglo de 20'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="rebus-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Ban className="w-10 h-10" />
            Productions Rebus
          </h1>
          <p className="text-sm text-slate-600 mt-1">Productions défectueuses qui ne seront pas vendues</p>
        </div>
        <Button
          onClick={() => setShowNewRebusModal(true)}
          data-testid="new-rebus-button"
          className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-sm shadow-lg h-12 px-6"
        >
          <Plus className="w-5 h-5 mr-2" />
          Déclarer un Rebus
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {['agglo_10', 'agglo_15', 'agglo_20', 'agglo_hourdis_16', 'agglo_plein_15'].map((type) => {
          const data = summary[type] || { total_quantity: 0, count: 0 };
          return (
            <Card key={type} className="bg-white border border-red-200 rounded-sm p-6" data-testid={`rebus-summary-${type}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-[#1B3268]">{getAggloTypeLabel(type)}</h4>
                  <p className="text-xs text-slate-500">{data.count} déclaration(s)</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-red-600">{data.total_quantity}</p>
                  <p className="text-xs text-slate-500">unités</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Rebus Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="rebus-table">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Historique des Rebus</h3>
          <p className="text-sm text-slate-500 mt-1">Liste des productions défectueuses déclarées</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Type d'Agglo</th>
                <th className="p-4 text-right">Quantité</th>
                <th className="p-4 text-left">Raison</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rebusList.map((rebus) => (
                <tr key={rebus.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`rebus-row-${rebus.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">
                        {new Date(rebus.date).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      rebus.agglo_type === 'agglo_10' ? 'bg-blue-100 text-blue-800' :
                      rebus.agglo_type === 'agglo_15' ? 'bg-green-100 text-green-800' :
                      rebus.agglo_type === 'agglo_20' ? 'bg-purple-100 text-purple-800' :
                      rebus.agglo_type === 'agglo_hourdis_16' ? 'bg-orange-100 text-orange-800' :
                      'bg-pink-100 text-pink-800'
                    }`}>
                      {getAggloTypeLabel(rebus.agglo_type)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-2xl font-bold text-red-600">{rebus.quantity}</span>
                    <span className="text-xs text-slate-500 ml-1">unités</span>
                  </td>
                  <td className="p-4">
                    <span className="text-slate-600">{rebus.reason || '-'}</span>
                  </td>
                  <td className="p-4 text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRebusToDelete(rebus)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-rebus-${rebus.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rebusList.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              Aucun rebus enregistré
            </div>
          )}
        </div>
      </Card>

      {/* New Rebus Modal */}
      <Dialog open={showNewRebusModal} onOpenChange={setShowNewRebusModal}>
        <DialogContent className="sm:max-w-[450px]" data-testid="new-rebus-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Déclarer un Rebus
            </DialogTitle>
            <DialogDescription>
              Enregistrez les productions défectueuses qui ne pourront pas être vendues.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date" className="text-sm font-medium text-slate-700">
                <Calendar className="w-4 h-4 inline mr-1" />
                Date
              </Label>
              <Input
                id="date"
                type="date"
                value={newRebus.date}
                onChange={(e) => setNewRebus({...newRebus, date: e.target.value})}
                data-testid="new-rebus-date"
                className="border-slate-300"
              />
            </div>

            {/* Type d'agglo */}
            <div className="space-y-2">
              <Label htmlFor="aggloType" className="text-sm font-medium text-slate-700">
                <Package className="w-4 h-4 inline mr-1" />
                Type d'Agglo
              </Label>
              <Select 
                value={newRebus.aggloType} 
                onValueChange={(value) => setNewRebus({...newRebus, aggloType: value})}
              >
                <SelectTrigger data-testid="new-rebus-agglo-type" className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agglo_10">Agglo de 10</SelectItem>
                  <SelectItem value="agglo_15">Agglo de 15</SelectItem>
                  <SelectItem value="agglo_20">Agglo de 20</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quantité */}
            <div className="space-y-2">
              <Label htmlFor="quantity" className="text-sm font-medium text-slate-700">
                Quantité défectueuse *
              </Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                placeholder="Ex: 50"
                value={newRebus.quantity}
                onChange={(e) => setNewRebus({...newRebus, quantity: e.target.value})}
                data-testid="new-rebus-quantity"
                className="border-slate-300 text-lg font-medium"
              />
            </div>

            {/* Raison */}
            <div className="space-y-2">
              <Label htmlFor="reason" className="text-sm font-medium text-slate-700">
                Raison (optionnel)
              </Label>
              <Input
                id="reason"
                type="text"
                placeholder="Ex: Cassés pendant le séchage, mal formés..."
                value={newRebus.reason}
                onChange={(e) => setNewRebus({...newRebus, reason: e.target.value})}
                data-testid="new-rebus-reason"
                className="border-slate-300"
              />
            </div>

            {/* Info */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Impact sur le stock</p>
                <p className="text-amber-700">Ces unités seront déduites du stock disponible à la vente.</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewRebusModal(false)}
              data-testid="cancel-new-rebus"
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateRebus}
              disabled={saving}
              data-testid="save-new-rebus"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer Rebus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!rebusToDelete} onOpenChange={() => setRebusToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Supprimer ce rebus ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
              {rebusToDelete && (
                <div className="space-y-2 mt-2">
                  <span className="block">Vous êtes sur le point de supprimer le rebus suivant :</span>
                  <div className="bg-slate-50 p-3 rounded-md text-sm">
                    <span className="block"><strong>Type :</strong> {getAggloTypeLabel(rebusToDelete.agglo_type)}</span>
                    <span className="block"><strong>Quantité :</strong> {rebusToDelete.quantity} unités</span>
                    <span className="block"><strong>Date :</strong> {new Date(rebusToDelete.date).toLocaleDateString('fr-FR')}</span>
                    {rebusToDelete.reason && <span className="block"><strong>Raison :</strong> {rebusToDelete.reason}</span>}
                  </div>
                  <span className="block text-green-600 font-medium mt-3">
                    Les unités seront réintégrées au stock.
                  </span>
                </div>
              )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRebus}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RebusPage;
